/**
 * English for the settings and admin screens — the workspace, alerts, the
 * change history, flows and their templates, tag manager, detectors, the
 * edge, IP intel. Spread into `en`, so the keys are part of `TKey` and
 * `ru.admin.ts` must cover every one of them.
 */
export const enAdmin = {
  "ws.saved": "Workspace settings saved",
  "ws.saveFailed": "Save failed",
  "ws.loading": "Loading workspace…",
  "ws.title": "Workspace",
  "ws.subtitle": "Identity and defaults shared by everyone in this workspace.",
  "ws.name": "Name",
  "ws.people": "People and roles",
  "ws.people.hint": "Who works in the tracker and with which role: Admin, Buyer or Viewer. The organization's owners and admins grant roles in the console.",
  "ws.people.open": "Members and roles",
  "ws.domain": "Default tracking domain",
  "ws.domain.hint": "New campaigns inherit this domain for their /clk links.",
  "ws.preset": "Default preset",
  "ws.preset.hint": "Picks the system report presets and goal seeds this workspace defaults to.",
  "ws.preset.none": "No default",
  "ws.tz": "Timezone",
  "ws.tz.hint": "Every report of this workspace counts its days in this zone unless the page names another; a lead stays on the day it arrived there.",
  "ws.slug": "Slug",
  "ws.status": "Status",
  "ws.org": "Keycloak organization",
  "ws.save": "Save changes",
  "ws.readOnly": "You have read-only access — ask an admin to change workspace settings.",
  "ws.status.active": "Active",
  "ws.status.suspended": "Suspended",
  "ws.status.archived": "Archived",
  "audit.kind.workspace": "Workspace",
  "audit.kind.trafficSource": "Traffic source",
  "audit.kind.network": "Partner network",
  "audit.kind.offer": "Offer",
  "audit.kind.lander": "Lander",
  "audit.kind.campaign": "Campaign",
  "audit.kind.flow": "Flow",
  "audit.kind.flowTemplate": "Flow template",
  "audit.kind.domain": "Tracking domain",
  "audit.kind.integration": "Integration",
  "audit.kind.snippet": "Tag manager snippet",
  "audit.kind.alertRule": "Alert rule",
  "audit.kind.rule": "Rule",
  "audit.kind.contentSet": "Content set",
  "audit.verb.create": "created",
  "audit.verb.update": "changed",
  "audit.verb.pause": "paused",
  "audit.verb.activate": "switched on",
  "audit.verb.archive": "archived",
  "audit.verb.duplicate": "copied",
  "audit.verb.delete": "deleted",
  "logs.col.time": "Time",
  "logs.col.actor": "Who",
  "logs.col.action": "Action",
  "logs.col.resource": "Object",
  "logs.system": "system",
  "logs.title": "Change history",
  "logs.subtitle": "Every change in the workspace: who, what and when. Rows are never edited or removed.",
  "logs.error": "Failed to load the history",
  "logs.allResources": "All objects",
  "logs.filter.actor": "Filter by person",
  "logs.filter.actor.ph": "Person id…",
  "logs.filter.action": "Filter by action",
  "logs.filter.action.ph": "Action (campaign.update)…",
  "logs.empty": "No changes in the selected period.",
  "logs.detail": "Change",
  "logs.detail.hint": "Pick a row to see who changed what, and how it was before and after.",
  "logs.by": "Who",
  "logs.changes": "Changes",
  "logs.noDiff": "No before/after recorded.",
  "logs.requestId": "Request ID",
  "logs.ua": "Browser",
  "rules.domain.click": "Routing",
  "rules.domain.metrics": "Alert",
  "rules.sev.info": "Info",
  "rules.sev.warning": "Warning",
  "rules.sev.critical": "Critical",
  "rules.ago.now": "just now",
  "rules.ago.m": "{n}m ago",
  "rules.ago.h": "{n}h ago",
  "rules.ago.d": "{n}d ago",
  "rules.ago.mo": "{n}mo ago",
  "rules.act.none": "No action",
  "rules.act.route.offer": "offer",
  "rules.act.route.lander": "lander",
  "rules.act.route.default": "default",
  "rules.act.route": "Route → {target}",
  "rules.act.block": "Block",
  "rules.act.notify": "Notify ({n})",
  "rules.event.observed": "observed {observed} · threshold {threshold}",
  "rules.title": "Rules",
  "rules.subtitle": "One model, two uses: routing rules decide where a click goes in a flow; alert rules watch the numbers and notify.",
  "rules.tab.library": "Library",
  "rules.tab.events": "Alerts fired",
  "rules.search": "Search rules by name…",
  "rules.new": "New rule",
  "rules.loadError": "Failed to load rules",
  "rules.filter.all": "All rules",
  "rules.filter.click": "Routing",
  "rules.filter.metrics": "Alerts",
  "rules.tags": "Tags",
  "rules.toast.on": "{name} switched on",
  "rules.toast.off": "{name} switched off",
  "rules.updateFailed": "Update failed",
  "rules.untitled": "Untitled rule",
  "rules.on": "On",
  "rules.off": "Off",
  "rules.toggle": "Switch {name} on or off",
  "rules.usedIn": "Used in",
  "rules.noMatch": "No rules match this filter.",
  "rules.empty.title": "Build your first rule",
  "rules.empty.body": "Rules are reusable. Make a routing filter for flow branches, or an alert that watches a metric over a window. Start from a template:",
  "rules.empty.routing": "Routing filter",
  "rules.empty.alert": "Metric alert",
  "rules.col.severity": "Severity",
  "rules.col.triggered": "Fired",
  "rules.col.rule": "Rule",
  "rules.col.summary": "Summary",
  "rules.col.ack": "Seen",
  "rules.acked": "seen",
  "rules.ackFailed": "Could not mark as seen",
  "rules.ack": "Mark seen",
  "rules.events.loadError": "Failed to load alerts",
  "rules.events.unacked": "Only not seen",
  "rules.events.allClear": "Nothing waiting. All clear.",
  "rules.events.none": "No alerts have fired yet.",
  "rule.ed.nameRequired": "Name is required",
  "rule.ed.updated": "Rule updated",
  "rule.ed.created": "Rule created",
  "rule.ed.saveFailed": "Save failed",
  "rule.ed.usedDetach": "Used by {n} flow(s) — detach first",
  "rule.ed.confirmDelete": "Delete rule \"{name}\"? Past events stay in the log.",
  "rule.ed.deleted": "Rule deleted",
  "rule.ed.deleteFailed": "Delete failed",
  "rule.ed.edit": "Edit rule",
  "rule.ed.desc": "A condition and what to do when it holds. Routing decides a click's branch in a flow; an alert notifies when a metric crosses a threshold.",
  "rule.ed.save": "Save",
  "rule.ed.create": "Create",
  "rule.ed.cancel": "Cancel",
  "rule.ed.delete": "Delete",
  "rule.ed.name": "Name",
  "rule.ed.name.ph.metrics": "CPA watch — US health",
  "rule.ed.name.ph.click": "Block bots — desktop",
  "rule.ed.kind": "Kind",
  "rule.ed.description": "Description",
  "rule.ed.description.ph": "What this rule is for (optional).",
  "rule.ed.condition": "Condition",
  "rule.ed.usedBy": "Used by {n} flow(s) — it cannot be deleted until detached.",
  "rule.ai.none": "The assistant returned no rule — try rephrasing",
  "rule.ai.drafted": "Drafted a rule",
  "rule.ai.review": "Draft needs review",
  "rule.ai.connect": "Connect {provider} first to use the assistant",
  "rule.ai.failed": "Suggestion failed",
  "rule.ai.ph.metrics": "Describe an alert… e.g. \"warn me if spend jumps 50% in an hour\"",
  "rule.ai.ph.click": "Describe the rule… e.g. \"mobile US/CA traffic, excluding bots\"",
  "rule.ai.drafting": "Drafting…",
  "rule.ai.draft": "Draft",
  "rule.act.channelType": "Channel type",
  "rule.act.removeChannel": "Remove channel",
  "rule.act.addChannel": "Add channel",
  "rule.act.legend": "Action (optional)",
  "rule.act.hint": "A routing rule usually decides a flow branch where it is referenced. An explicit route or block action overrides the destination when the rule matches.",
  "rule.act.kind": "Action",
  "rule.act.kind.none": "No action (match only)",
  "rule.act.kind.route": "Route to…",
  "rule.act.blockReason": "Block reason",
  "rule.act.blockReason.ph": "Bot traffic",
  "cond.metric": "Metric",
  "cond.expand": "Expand group",
  "cond.collapse": "Collapse group",
  "cond.and": "AND",
  "cond.or": "OR",
  "cond.emptyGroup": "Empty group. Add a condition below.",
  "cond.remove": "Remove condition",
  "cond.addCondition": "Condition",
  "cond.addGroup": "Group",
  "cond.notHint": "Inverts the wrapped condition.",
  "cond.sec.logic": "Logic",
  "cond.sec.audience": "Audience",
  "cond.sec.request": "Request",
  "cond.sec.signals": "Signals",
  "cond.sec.power": "Power user",
  "cond.removeValue": "Remove {v}",
  "cond.f.groupAnd": "All of (AND)",
  "cond.f.groupAnd.hint": "Match every child",
  "cond.f.groupOr": "Any of (OR)",
  "cond.f.groupOr.hint": "Match at least one child",
  "cond.f.not": "Not",
  "cond.f.not.hint": "Invert the wrapped condition",
  "cond.f.all": "Always match",
  "cond.f.all.hint": "Tautology (always true)",
  "cond.f.country": "Country",
  "cond.f.country.hint": "ISO-3166 alpha-2 codes",
  "cond.f.language": "Language",
  "cond.f.language.hint": "Accept-Language primary tag",
  "cond.f.device": "Device",
  "cond.f.device.hint": "mobile / tablet / desktop",
  "cond.f.match": "Field of the visit",
  "cond.f.match.hint": "region, city, ISP, OS, browser, IP, UA, referrer…",
  "cond.f.urlParam": "URL param",
  "cond.f.urlParam.hint": "?name=value",
  "cond.f.header": "Request header",
  "cond.f.header.hint": "name: value",
  "cond.f.botScore": "Bot score",
  "cond.f.botScore.hint": "0-100, with operator",
  "cond.f.timeOfDay": "Time of day",
  "cond.f.timeOfDay.hint": "UTC hour range",
  "cond.f.weightSplit": "Weighted split",
  "cond.f.weightSplit.hint": "Hash-bucket roll for A/B",
  "cond.f.expression": "Expression",
  "cond.f.expression.hint": "expr-lang escape hatch",
  "cond.f.botSignal": "Bot signal",
  "cond.f.botSignal.hint": "Datacenter / Tor / VPN / anomaly",
  "cond.f.spendSpike": "Spend spike",
  "cond.f.spendSpike.hint": "% jump vs baseline",
  "cond.f.conversionDrop": "Conversion drop",
  "cond.f.conversionDrop.hint": "% fall vs baseline",
  "cond.f.roiBelow": "ROI below",
  "cond.f.roiBelow.hint": "ratio floor",
  "cond.f.zeroConversions": "Zero conversions",
  "cond.f.zeroConversions.hint": "no conv in window",
  "cond.f.cpaAbove": "CPA above",
  "cond.f.cpaAbove.hint": "max cost-per-acq",
  "cond.op.eq": "= equals",
  "cond.op.lt": "< less than",
  "cond.op.lte": "≤ at most",
  "cond.op.gt": "> greater than",
  "cond.op.gte": "≥ at least",
  "cond.sig.bot": "Generic bot flag",
  "cond.sig.dc": "Datacenter IP",
  "cond.sig.tor": "Tor exit node",
  "cond.sig.vpn": "Known VPN",
  "cond.sig.ua": "UA anomaly",
  "cond.m.spendSpike.help": "Fires when spend grows by at least this percentage versus the comparison period.",
  "cond.m.conversionDrop.help": "Fires when conversions fall by at least this percentage versus the comparison period.",
  "cond.m.roiBelow.help": "Fires when the ROI ratio over the window drops below this value.",
  "cond.m.zeroConversions.help": "Fires when the window passes with traffic but no conversions. No threshold.",
  "cond.m.cpaAbove.help": "Fires when cost-per-acquisition over the window exceeds this amount.",
  "cond.expected": "Expected value",
  "cond.threshold": "Threshold",
  "cond.threshold.none": "none — window only",
  "cond.window": "Window (minutes)",
  "cond.compare": "Comparison period",
  "cond.compare.day": "Previous day",
  "cond.compare.week": "Previous week",
  "cond.windowOf": "{w} window",
  "cond.s.negated": "negated",
  "cond.s.always": "always",
  "cond.all.body": "Always evaluates true. Useful as a placeholder.",
  "cond.s.noCountries": "no countries",
  "cond.country.codes": "ISO country codes",
  "cond.s.noLanguages": "no languages",
  "cond.language.tags": "Language tags",
  "cond.s.noDevices": "no devices",
  "cond.device.kinds": "Device kinds",
  "cond.s.noName": "no name",
  "cond.param.name": "Param name",
  "cond.s.noHeader": "no header",
  "cond.header.name": "Header name",
  "cond.s.ranges": "{n} range(s)",
  "cond.operator": "Operator",
  "cond.match.field": "Field",
  "cond.match.values": "Values",
  "cond.threshold100": "Threshold (0-100)",
  "cond.hourStart": "Hour start (UTC)",
  "cond.hourEnd": "Hour end (UTC)",
  "cond.s.bucket": "{n}% bucket",
  "cond.bucket": "Bucket weight (0-100%)",
  "cond.bucket.hint": "Visitor ids are hashed deterministically; the rule fires for the first weight% of buckets.",
  "cond.s.noExpression": "no expression",
  "cond.expr.label": "Expression (expr-lang)",
  "cond.expr.hint": "For the rare case the structured kinds cannot express. Prefer them when possible.",
  "cond.signal": "Signal",
  "cond.signal.hint": "Fires when this bot signal is true for the request.",
  "cond.sum.spend": "Spend",
  "cond.sum.conversions": "Conversions",
  "cond.sum.not": "NOT",
  "cond.sum.country": "country",
  "cond.sum.lang": "lang",
  "cond.sum.device": "device",
  "flows.mode.single": "Single offer",
  "flows.mode.split": "Split test",
  "flows.mode.weighted": "Weighted rotator",
  "flows.mode.rules": "Rule-based",
  "flows.mode.template": "From a template",
  "flows.col.name": "Name",
  "flows.col.mode": "Mode",
  "flows.col.nodes": "Nodes",
  "flows.nodesEdges": "{nodes} / {edges} edges",
  "flows.col.actions": "Actions",
  "flows.edit": "Edit",
  "flows.archived": "Flow archived",
  "flows.archiveFailed": "Archive failed",
  "flows.archive": "Archive",
  "flows.title": "Flows",
  "flows.subtitle": "Routing graphs. A campaign is bound to one flow; the router walks it to pick the lander and the offer for a click.",
  "flows.new": "New flow",
  "flows.loadError": "Failed to load flows",
  "flows.search": "Search flows",
  "flows.search.ph": "Search flows…",
  "flows.empty.title": "No flows yet",
  "flows.empty.body": "Pick a flow template to start from a working recipe (offers by geo, an A/B split of landers, a bot shield…) or build one from scratch.",
  "flows.empty.templates": "Browse flow templates",
  "flows.empty.blank": "Start a blank flow",
  "flows.noMatch": "No flows match this search.",
  "node.lander": "Lander",
  "node.lander.desc": "Show a lander or prelander page before the offer.",
  "node.offer": "Offer",
  "node.offer.desc": "Send the click to a tracked offer URL.",
  "node.network": "Network",
  "node.network.desc": "Hand off to an affiliate network's smartlink.",
  "node.filter": "Filter",
  "node.filter.desc": "Gate subsequent nodes by a CEL expression.",
  "node.redirect": "Redirect",
  "node.redirect.desc": "Send the visitor to an arbitrary URL.",
  "node.branch": "Branch",
  "node.branch.desc": "A second start: where a visitor goes on Back, exit intent, idle or scroll end. The page catches the moment; connect it to the branch's landers or offers.",
  "node.botRule": "Bot rule",
  "node.botRule.desc": "Bounce bots before they reach the offer.",
  "node.safePage": "Safe page",
  "node.safePage.desc": "Terminal that serves a compliance-safe URL (cloaker safe page). Distinct from a redirect so analytics can split cloaker hits from money 302s.",
  "node.jsChallenge": "JS challenge",
  "node.jsChallenge.desc": "Non-terminal interstitial. Serves a JS+canvas+timing page; on solve the visitor gets a signed cookie + redirect back into the walk. Headless scrapers fail closed and never reach the next node.",
  "node.fpGate": "Fingerprint gate",
  "node.fpGate.desc": "Multi-signal cloaker scorer. Fuses TLS JA4, UA, and header signals into a score, then routes by band — low/medium continue the walk, high band exits to a safe page.",
  "node.unknown": "Unknown",
  "node.role.pre": "Prelander",
  "node.role.pre.hint": "Warm-up page shown before the main landing.",
  "node.role.landing": "Landing",
  "node.role.landing.hint": "The primary money page with the CTA.",
  "node.role.thanks": "Thank-you",
  "node.role.thanks.hint": "Post-conversion confirmation page.",
  "node.branch.noTrigger": "no trigger",
  "node.branch.idle": "{label} · {n}s",
  "palette.pages": "Pages",
  "palette.logic": "Logic",
  "palette.exits": "Exits",
  "palette.branches": "Branches",
  "palette.add": "Add node",
  "palette.search": "Search nodes…",
  "palette.search.label": "Search node kinds",
  "palette.noMatch": "No nodes match “{q}”.",
  "palette.dragHint": "Click to append, or drag onto the canvas to place",
  "insp.title": "Inspector",
  "insp.empty": "Select a node on the canvas to edit its properties.",
  "insp.node": "Node",
  "insp.terminal": "terminal",
  "insp.start": "start",
  "insp.close": "Close inspector",
  "insp.tab": "Tab",
  "insp.tab.config": "Config",
  "insp.tab.context": "Context",
  "insp.tab.rules": "Rules",
  "insp.makeStart": "Make this the start node",
  "insp.nodeContext": "Node context",
  "insp.remove": "Remove",
  "insp.done": "Done",
  "insp.lander.hint": "Pick a real lander or reference a parameter as ${name}.",
  "insp.role": "Funnel role",
  "insp.role.hint": "Labels this step in the campaign funnel (prelander → landing → thank-you). Descriptive only — routing ignores it.",
  "insp.offer.hint": "Pick a real offer or reference a parameter as ${name}.",
  "insp.network": "Network ID",
  "insp.network.hint": "Affiliate network UUID or ${param}.",
  "insp.redirect": "Redirect URL",
  "insp.trigger": "Trigger",
  "insp.trigger.hint": "The page runtime catches it and sends the visitor in. One branch per trigger.",
  "insp.trigger.ph": "Pick a trigger",
  "insp.idle": "Idle seconds",
  "insp.idle.hint": "No input for this long ({min}–{max}).",
  "insp.safeUrl": "Safe-page URL",
  "insp.cookie": "Pass-cookie name",
  "insp.cookie.hint": "Set by /jsc/verify when the visitor solves the challenge. Use distinct names if a flow has multiple challenge nodes.",
  "insp.cookieTtl": "Pass-cookie TTL (s)",
  "insp.cookieTtl.hint": "0 falls back to the server default (1800s).",
  "insp.sort": "Sort order",
  "insp.weight": "Weight",
  "insp.weight.typed": "Used for a probabilistic split when this node is one of several targets.",
  "insp.weight.other": "0–100. Rotators distribute traffic by target weight.",
  "insp.enabled": "Enabled",
  "insp.fp.hint": "Signal weights are independent magnitudes (not a distribution). Leave at 0 to use the server defaults — equal thirds, bands at 0.33 / 0.66.",
  "insp.fp.ja4": "JA4 weight",
  "insp.fp.ua": "UA weight",
  "insp.fp.header": "Header weight",
  "insp.fp.low": "Low threshold",
  "insp.fp.low.hint": "Score below ⇒ low band (continues).",
  "insp.fp.high": "High threshold",
  "insp.fp.high.hint": "Score at/above ⇒ high band (safe page).",
  "insp.rules.bot": "Bot signals fire this rule before the click reaches a terminal. A hit goes straight to a redirect.",
  "insp.rules.filter": "A condition decides which edges the click may take. Combine AND/OR/NOT groups.",
  "insp.param": "Parameter binding",
  "insp.clear": "Clear",
  "insp.newParam": "+ new parameter",
  "insp.sum": "Σ {sum}/100 across {n} siblings",
  "insp.autoDistribute": "Auto-distribute",
  "tpl.scope.ws": "Workspace",
  "tpl.scope.ws.hint": "Visible only to your workspace.",
  "tpl.scope.org": "Organization",
  "tpl.scope.org.hint": "Shared across every workspace in your org.",
  "tpl.scope.global": "Global",
  "tpl.scope.global.hint": "Featured in the gallery for every tenant.",
  "bp.fromFlow.name": "{name} (template)",
  "bp.fromFlow.desc": "Captured from flow {id}",
  "bp.nameRequired.tpl": "Template name is required",
  "bp.nameRequired.flow": "Flow name is required",
  "bp.needNode": "Add at least one node",
  "bp.fixErrors": "Fix validation errors before saving",
  "bp.tplUpdated.rerouted": "Template updated — re-routed {n} campaign(s)",
  "bp.tplUpdated": "Template updated",
  "bp.keptRouting": "A campaign kept its previous routing: {message}",
  "bp.tplCreated": "Template created",
  "bp.flowSaved": "Flow saved",
  "bp.flowCreated": "Flow created",
  "bp.back.templates": "Templates",
  "bp.untitled.tpl": "Untitled template",
  "bp.untitled.flow": "Untitled flow",
  "bp.mode.single": "Single",
  "bp.mode.rotator": "Rotator",
  "bp.startAt": "Start at",
  "bp.startAt.ph": "Pick start node",
  "bp.commands": "Command palette",
  "bp.undo": "Undo structural change (⌘Z)",
  "bp.redo": "Redo structural change (⌘⇧Z)",
  "bp.details.hide": "Hide details",
  "bp.details": "Details",
  "bp.saveAsTpl.hint": "Save this flow's shape as a reusable template",
  "bp.saveAsTpl": "Save as template",
  "bp.fixFirst": "Fix validation errors first",
  "bp.saving": "Saving…",
  "tpl.category": "Category",
  "tpl.scope": "Scope",
  "tpl.tags.ph": "Add tags (e.g. health, geo-us)…",
  "tpl.desc.ph": "What does this template do, and when should one pick it?",
  "bp.palette": "Node palette",
  "bp.canvas": "Flow canvas",
  "bp.empty.tpl": "Empty template",
  "bp.empty.flow": "Empty flow",
  "bp.empty.tpl.body": "Pick a node from the palette to start building the template.",
  "bp.empty.flow.body": "Pick a node from the palette, start from a recipe, or browse templates.",
  "bp.recipe": "Start from a recipe",
  "bp.flowContext": "Flow context",
  "bp.routing": "Routing behavior",
  "bp.sticky": "Sticky offer",
  "bp.sticky.hint": "Pin each visitor's first-rolled offer so revisits, CTA clicks, and every branch all resolve to the same offer.",
  "bp.sticky.ttl": "TTL (hours)",
  "bp.sticky.ttl.hint": "Default 24h, max 30 days.",
  "bp.rotation": "Time rotation",
  "bp.rotation.hint": "Within the window a returning visitor sees the same lander; past it, the last lander is excluded and a fresh one is rolled.",
  "val.missingLander": "Add at least one Lander node — the flow has no page to show.",
  "val.missingOffer": "Add at least one Offer node — the flow has nothing to monetise.",
  "val.branch.named": "The «{trigger}» branch",
  "val.branch.any": "A branch",
  "val.branch.pickTrigger": "Pick what opens this branch: Back, exit intent, idle or scroll to end.",
  "val.branch.idle": "An idle branch fires after {min}–{max} seconds without input.",
  "val.branch.incoming": "{name} has an incoming edge — a branch is entered by its trigger, never walked into.",
  "val.branch.default": "A branch cannot be the start node — the ad click starts the main journey.",
  "val.branch.empty": "{name} leads nowhere — connect it to its lander(s) or offers.",
  "val.branch.dup": "The flow already has a «{trigger}» branch — one branch per trigger.",
  "val.unreachable": "{kind} is unreachable — no path leads to it from the start node.",
  "val.terminal": "{kind} is a terminal node and must not have outgoing edges.",
  "val.mixed": "Fanout cannot mix conditional and unconditional edges. Pick one pattern.",
  "val.sum": "Outgoing target weights must sum to 100 (got {sum}).",
  "val.cycle": "Flow contains a cycle. Edges must form a DAG.",
  "canvas.tb": "Top-to-bottom layout",
  "canvas.vertical": "Vertical",
  "canvas.lr": "Left-to-right layout",
  "canvas.horizontal": "Horizontal",
  "canvas.tidy.hint": "Auto-arrange (discards manual positions)",
  "canvas.tidy": "Re-tidy",
  "canvas.wireHint": "Drag from a node's bottom dot to another node's top dot to connect them.",
  "canvas.dropHint": "Drop on another node's top handle to create the connection",
  "canvas.edge": "Edge",
  "canvas.noFilters": "No filter nodes in this flow yet — drop one from the palette to put a condition on this edge.",
  "canvas.always": "Always (unconditional)",
  "canvas.condHint": "When set, the click takes this edge only if the filter holds at click time.",
  "canvas.removeEdge": "Remove edge",
  "canvas.newHere": "New node here",
  "canvas.noRule": "no rule",
  "card.unbound": "Unbound",
  "card.noUrl": "no url",
  "card.on": "on {trigger}",
  "card.start.hint": "Start node — the click router enters here",
  "card.disabled": "Disabled",
  "card.terminal.hint": "Terminal — no outgoing edges",
  "card.hasErrors": "Has validation errors",
  "card.errorLabel": "Validation error: {codes}",
  "card.invalid": "invalid",
  "card.dropHere": "Drop a connection here",
  "card.dragFrom": "Drag from here to another node to add a connection",
  "card.fanout": "{n} branches • Σ weight = {sum}",
  "edge.split": "{n}% of the split",
  "edge.if": "if {cond}",
  "vb.ok": "Flow is valid",
  "vb.issues": "Issues blocking save: {n}",
  "vb.error": "ERROR",
  "ceb.title": "The flow canvas crashed",
  "ceb.unexpected": "An unexpected error broke the editor.",
  "ceb.hint": "Reset the canvas to retry. Node edits made before the crash were kept in memory and reappear after the reset.",
  "ceb.reset": "Reset canvas",
  "param.text": "Text",
  "param.number": "Number",
  "param.title": "Parameters",
  "param.hint": "Bound when the template is applied. Reference one in a node as",
  "param.add": "Add parameter",
  "param.empty": "No parameters yet. Use the «+ new parameter» chip in the inspector to turn any node binding into a parameter.",
  "param.remove": "Remove parameter",
  "param.key": "Key",
  "param.kind": "Kind",
  "param.label": "Label",
  "param.default": "Default",
  "param.default.hint": "Optional fallback when unbound",
  "cw.title": "Start from a template",
  "cw.desc": "Working recipes — geo splits, A/B tests, bot shields. Pick one, fill the slots, and keep editing from there.",
  "cw.none": "No templates yet — make one from any flow with «Save as template».",
  "cw.curated": "Curated",
  "cw.another": "Pick another",
  "cw.noParams": "No parameters — the template applies as-is.",
  "cw.use": "Use template",
  "aw.creating": "Creating flow…",
  "aw.create": "Create flow",
  "aw.review": "Review",
  "aw.next": "Next",
  "aw.back": "Back",
  "aw.noParams": "No parameters required",
  "aw.noParams.hint": "Apply the template to create a flow as-is.",
  "aw.blueprint": "Template",
  "aw.nodes": "{n} node(s)",
  "aw.step": "Step {i} of {n}",
  "aw.default": "Default:",
  "aw.ready": "Ready to create",
  "aw.ready.hint": "Check the values below — a flow is created from this template with them.",
  "aw.unbound": "not set",
  "aw.noParams.review": "This template has no parameters — the flow is created as-is.",
  "tc.noDesc": "No description provided.",
  "tc.actions": "Template actions",
  "tc.edit": "Edit template",
  "tc.params": "{n} param(s)",
  "tc.apply": "Apply",
  "vt.current": "current",
  "vh.restored": "Restored version {v}",
  "vh.restoreFailed": "Restore failed",
  "vh.title": "Version history",
  "vh.desc": "Every save snapshots the template. Restoring adds a new version on top, so a rollback can itself be undone.",
  "vh.restoring": "Restoring…",
  "vh.restoreV": "Restore v{v}",
  "vh.close": "Close",
  "vh.timeline": "Timeline",
  "vh.saving": "saving…",
  "vh.none": "No versions yet",
  "vh.none.hint": "The first save creates v1. Until then there's nothing to restore.",
  "vh.diff": "Diff: v{v} → live",
  "vh.restore.hint": "The current template is kept as a new version before the snapshot replaces it, so the rollback can be undone.",
  "vh.note": "Rollback note (optional)",
  "vh.note.ph": "e.g. revert the test after the CTR drop",
  "vh.open": "Open version history",
  "vh.history": "History",
  "mini.graph": "Flow graph, {n} node(s)",
  "ctx.strict": "strict",
  "ctx.enabled": "Layer enabled",
  "ctx.enabled.hint": "When off, this layer is the last one — the layers after it are not merged.",
  "ctx.any": "any",
  "ctx.unknown": "unknown",
  "ctx.csv": "comma-separated values",
  "ctx.addKey": "Add key",
  "ctx.fromSchema": "From schema",
  "ctx.customKey": "Custom key",
  "ctx.add": "Add",
  "ctx.schema": "Schema",
  "ctx.strictMode": "Strict mode",
  "ctx.transform": "Context transform",
  "cmd.ph": "Type a command or search nodes…",
  "cmd.none": "No matches.",
  "cmd.toggle": "Toggle layout direction",
  "cmd.goto": "Go to node",
  "cmd.setStart": "Set start node",
  "as.describeFirst": "Describe the funnel first",
  "as.none": "The assistant returned no draft — try rephrasing",
  "as.connect": "Connect {provider} first",
  "as.openConnections": "Open integrations",
  "as.applied": "Draft applied — ⌘Z to undo",
  "as.button": "Assistant",
  "as.title": "Flow assistant",
  "as.hint": "Describe the funnel in plain words — the assistant drafts the routing graph with offer and lander slots you fill in afterwards.",
  "as.ph": "e.g. \"US mobile traffic goes to the main offer through a prelander, bots to a safe page, everyone else to the fallback offer\"",
  "as.again": "Draft again",
  "as.draft": "Draft a flow",
  "as.untitled": "Assistant draft",
  "as.issues": "The draft has {n} open issue(s) — they show on the canvas after applying.",
  "as.ok": "The draft passes the checks — fill the slots and save.",
  "as.apply": "Apply as draft",
  "ubd.param": "Parameter",
  "ft.confirmArchive": "Archive \"{name}\"?",
  "ft.archived": "Archived \"{name}\"",
  "ft.created": "Flow \"{name}\" created from the template",
  "ft.createFailed": "Could not create the flow",
  "ft.eyebrow": "Templates",
  "ft.title": "Flow templates",
  "ft.subtitle": "Reusable flows with lander, offer and country slots. Apply a template to get a ready flow for a campaign in seconds.",
  "ft.new": "New template",
  "ft.search": "Search templates by name…",
  "ft.all": "All",
  "ft.loadError": "Failed to load templates",
  "ft.noMatch": "No matching templates",
  "ft.empty": "No flow templates yet",
  "ft.noMatch.hint": "Try a different search or clear the filters above.",
  "ft.empty.hint": "A template keeps a flow's shape with named parameters, so a new campaign launches without rebuilding the routing every time.",
  "ft.clear": "Clear filters",
  "ft.first": "Create the first template",
  "goals.unarchived": "\"{key}\" restored from the archive",
  "goals.archived": "\"{key}\" archived",
  "goals.confirmDelete": "Delete goal \"{key}\"? Past conversions keep the key.",
  "goals.deleted": "Goal \"{key}\" deleted",
  "goals.inArchive": "archived",
  "goals.col.revenue": "Revenue",
  "goals.col.primary": "Primary",
  "goals.col.multiple": "Repeats",
  "goals.unarchive": "Restore",
  "goals.title": "Conversion goals",
  "goals.subtitle": "What a conversion means in this workspace — the keys drive postback mapping, dedup, revenue and per-goal report metrics.",
  "goals.seed": "Add from preset",
  "goals.presets": "Presets",
  "goals.seeded": "Added {n} goals from «{preset}»",
  "goals.seedFailed": "Could not add the preset",
  "goals.count": "{n} goals",
  "goals.noPresets": "No presets available",
  "goals.new": "New goal",
  "goals.loadError": "Failed to load goals",
  "goals.showArchived": "Show archived goals",
  "goals.empty": "No goals yet. Add a preset or create the first goal by hand.",
  "goals.updated": "Goal saved",
  "goals.created": "Goal created",
  "goals.editTitle": "Goal · {key}",
  "goals.newTitle": "New conversion goal",
  "goals.key.immutable": "The key cannot change — it is written on past conversion events.",
  "goals.key.new": "The key is written on every conversion event and does not change later.",
  "goals.name.ph": "Deposit",
  "goals.revenue": "Counts as revenue",
  "goals.revenue.hint": "Payouts on this goal go into the revenue metric.",
  "goals.primary": "Primary goal",
  "goals.primary.hint": "Drives the headline conversions and CR (one per workspace).",
  "goals.multiple": "Allow repeats",
  "goals.multiple.hint": "Several events per click count (e.g. rebills).",
  "goals.create": "Create goal",
  "goals.cat.registration": "Registration",
  "goals.cat.deposit": "Deposit",
  "goals.cat.rebill": "Rebill",
  "goals.cat.sale": "Sale",
  "goals.cat.lead": "Lead",
  "goals.cat.upsell": "Upsell",
  "goals.cat.install": "Install",
  "goals.cat.trial": "Trial",
  "goals.cat.custom": "Custom",
  "tm.placement": "Placement",
  "tm.updated": "Updated",
  "tm.saved": "Snippet saved",
  "tm.created": "Snippet created",
  "tm.deleted": "Snippet deleted",
  "tm.saveFirstPublish": "Save the snippet first, then publish.",
  "tm.published": "Snippet published",
  "tm.publishFailed": "Publish failed",
  "tm.saveFirstTest": "Save the snippet first, then send a test event.",
  "tm.testSent": "Test event sent — {detail}",
  "tm.testFailed": "Test event failed: {detail}",
  "tm.testError": "Test send failed",
  "tm.title": "Tag manager",
  "tm.subtitle": "Code snippets inserted into landers, by traffic source or before a redirect. Conditions use the same expression engine as rules.",
  "tm.new": "New snippet",
  "tm.loadError": "Failed to load snippets",
  "tm.allScopes": "All scopes",
  "tm.enabledOnly": "Enabled only",
  "tm.empty": "No snippets yet.",
  "tm.edit": "Snippet",
  "tm.desc": "A snippet fires at the chosen place. Conditions narrow who gets it.",
  "tm.deleting": "Deleting…",
  "tm.target": "Target object",
  "tm.conditions": "Conditions (expression)",
  "tm.conditions.ph": "e.g. country == \"US\" && device == \"mobile\"",
  "tm.conditions.hint": "Leave empty to fire always. Checked against the request at fire time.",
  "tm.code": "Code",
  "tm.enabled.hint": "A disabled snippet is kept but never inserted.",
  "tm.consent": "Required consent (Consent Mode v2)",
  "tm.consent.hint": "Fires only when every checked category is granted in the visitor's consent.",
  "tm.publishedV": "Published v{v}",
  "tm.draftV": "Draft v{v}",
  "tm.sending": "Sending…",
  "tm.sendTest": "Send test event",
  "tm.publishing": "Publishing…",
  "tm.publish": "Publish",
  "tm.dest": "Server-side destination",
  "tm.dest.hint": "A postback snippet fires from the server through the chosen adapter. Secrets (api_secret_ref, access_token_ref) are AWS Secrets Manager keys, never raw secrets — e.g.",
  "tm.mappings": "Field mappings (JSON)",
  "tm.mappings.array": "An array of",
  "tm.mappings.hint": "Macros are filled at fire time. Standard PII fields (em, ph, fn, ln, ge, ct, st, zp, country, external_id) are SHA-256 hashed automatically; click ids (fbc, fbp) go as they are.",
  "tm.http.hint": "Custom HTTP uses the snippet's code field as the outbound URL. Nothing else to set.",
  "tm.status.draft": "Draft",
  "tm.status.published": "Published",
  "tm.status.archived": "Archived",
  "tm.dest.http": "Custom HTTP",
  "tm.dest.ga4": "Google Analytics 4 (MP)",
  "tm.dest.tiktok": "TikTok Events API",
  "tm.kind.gtm": "GTM",
  "tm.kind.pixel": "Pixel",
  "tm.kind.postback": "Postback",
  "tm.kind.js": "Custom JS",
  "tm.kind.html": "Custom HTML",
  "tm.place.head": "Head",
  "tm.place.bodyTop": "Body top",
  "tm.place.bodyBottom": "Body bottom",
  "tm.place.beforeRedirect": "Before redirect",
  "ipi.enterCidr": "Enter a CIDR (e.g. 203.0.113.0/24)",
  "ipi.added": "Override added",
  "ipi.addFailed": "Could not add the override",
  "ipi.title": "IP intelligence",
  "ipi.subtitle": "Region, proxy/VPN/datacenter, ASN and reputation — merged from every feed into one MMDB the edge reads locally. Rebuilt on a schedule, published to every edge and reloaded without a trip to the origin.",
  "ipi.rebuildStarted": "Rebuild started",
  "ipi.rebuildFailed": "Could not start the rebuild",
  "ipi.feeds": "Feed sources",
  "ipi.feeds.error": "Failed to load feed sources: {error}",
  "ipi.unknownError": "unknown error",
  "ipi.feeds.none": "No feed sources registered.",
  "ipi.col.source": "Source",
  "ipi.col.freshness": "Freshness",
  "ipi.col.lastSuccess": "Last success",
  "ipi.col.rows": "Rows",
  "ipi.failing": "failing",
  "ipi.servingLast": "(serving the last good data)",
  "ipi.fresh": "fresh",
  "ipi.notRun": "not run",
  "ipi.stale": "stale",
  "ipi.configure.hint": "Configure token / URL",
  "ipi.configure": "configure",
  "ipi.setup": "set up",
  "ipi.on": "{source} switched on",
  "ipi.off": "{source} switched off",
  "ipi.disableHint": "Switching a source off takes effect on the next rebuild.",
  "ipi.overrides": "Manual overrides",
  "ipi.overrides.hint": "Force a classification for a CIDR — it wins over feed data at rebuild time. The lever for a misclassified network.",
  "ipi.classification": "Classification",
  "ipi.reason": "Reason",
  "ipi.reason.ph": "why this override exists",
  "ipi.noOverrides": "No overrides.",
  "ipi.expired": "expired",
  "ipi.expires": "expires",
  "ipi.deleted": "Override deleted",
  "ipi.versions": "MMDB version history",
  "ipi.noBuilds": "No builds recorded.",
  "ipi.col.built": "Built",
  "ipi.col.version": "Version",
  "ipi.col.records": "Records",
  "ipi.col.size": "Size",
  "ipi.col.published": "Published",
  "ipi.notPublished": "not published",
  "ipi.configured": "{source} configured and switched on",
  "ipi.configureFailed": "Could not configure",
  "ipi.dialog.hint": "Stored encrypted. Configuring a source also switches it on — it runs on the next rebuild.",
  "ipi.saveEnable": "Save and switch on",
  "ipi.rebuilding": "Rebuild in progress…",
  "ipi.lastFailed": "The last rebuild failed",
  "ipi.upToDate": "Data up to date",
  "ipi.loading": "Loading…",
  "ipi.started": "Started",
  "ipi.lastRun": "Last run",
  "ipi.noRebuild": "No rebuild has run yet",
  "ipi.rebuildNow": "Rebuild now",
  "ipi.nextRun": "Next run",
  "ipi.cadence": "Cadence",
  "ipi.every": "every {d}",
  "ipi.publishedVersion": "Published version",
  "ipi.sourcesFailed": "{n} source(s) failed",
  "ipi.disagreements": "{n} geo disagreement(s)",
  "ipi.in.soon": "in <1 min",
  "ipi.in.m": "in {n} min",
  "ipi.in.h": "in {n} h",
  "ipi.in.d": "in {n} d",
  "ipi.dur.s": "{s}s",
  "ipi.dur.ms": "{m}m {s}s",
  "ipi.dur.d": "{n}d",
  "ipi.dur.h": "{n}h",
  "ipi.dur.m": "{n}m",
  "ipi.cls.none": "none (clean)",
  "ipi.cls.vpn": "VPN",
  "ipi.cls.public_proxy": "public proxy",
  "ipi.cls.residential_proxy": "residential proxy",
  "ipi.cls.tor": "Tor",
  "ipi.cls.datacenter": "datacenter",
  "ipi.cls.unknown": "unknown",
  "ipi.f.ipinfo.ph": "paste your ipinfo.io token",
  "ipi.f.ipinfo.hint": "Free at ipinfo.io — the Lite dataset (geo + ASN).",
  "ipi.f.ip2l.hint": "Your LITE download link (it includes your token).",
  "ipi.f.maxmind.hint": "A URL serving the raw .mmdb (not the tar.gz).",
  "edge.bareOrigin": "Bare origin",
  "edge.mmdb.object": "object store ({key})",
  "edge.mmdb.file": "local file ({path})",
  "edge.mmdb.headers": "edge headers (X-Ip-*)",
  "edge.title": "Edge",
  "edge.subtitle": "Where campaigns run, which revisions are published, and whether the latest changes have reached their deployment.",
  "edge.pub": "Campaign publications",
  "edge.pub.hint": "Published is the activated revision. Observed is the last revision a serving endpoint confirmed; it does not confirm every location.",
  "edge.pub.error": "Publication status unavailable",
  "edge.pub.none": "No portable deployments configured yet.",
  "edge.col.path": "Campaign path / deployment",
  "edge.col.desired": "Desired",
  "edge.col.observed": "Observed",
  "edge.pending": "Pending",
  "edge.checkQueued": "Publication check queued",
  "edge.checkFailed": "Could not queue the publication check",
  "edge.retry": "Retry",
  "edge.delivery": "Event and lead delivery",
  "edge.delivery.hint": "Accepted events stay in durable storage while delivery retries. Reports may lag while projections are pending. Updated every 15 seconds.",
  "edge.delivery.error": "Delivery status unavailable",
  "edge.delivery.none": "No delivery data available.",
  "edge.col.pendingEvents": "Pending events",
  "edge.col.pendingLeads": "Pending leads",
  "edge.col.attention": "Needs attention",
  "edge.col.pendingProj": "Pending projections",
  "edge.col.oldest": "Oldest pending",
  "edge.col.lastProj": "Last projected",
  "edge.deadFailed": "{events} events · {leads} leads",
  "edge.topology": "Topology",
  "edge.provider": "Provider",
  "edge.ipiSource": "IP data source",
  "edge.hotReload": "MMDB hot reload",
  "edge.kvPublish": "Edge config publish (CF KV)",
  "edge.jscSecret": "JS-challenge secret",
  "edge.latestMmdb": "Latest published MMDB",
  "edge.records": "{n} records",
  "edge.built": "built {when}",
  "edge.publishedAt": "published {when}",
  "edge.notPublished": "not yet published to the edge",
  "edge.propagation": "IP data propagation",
  "edge.propagation.hint": "Confirms the fresh MMDB reached every edge plane, so region and proxy lookups run locally in each region with no trip to the origin.",
  "edge.plane.cf": "Cloudflare isolate edge",
  "edge.plane.origin": "Origin object store (Go edge)",
  "edge.objectOff": "object-store IP data not enabled",
  "edge.domainSync": "Domain config sync",
  "edge.domainSync.none": "No domain has synced edge config yet.",
  "edge.lastSynced": "Last synced",
  "edge.failed": "failed",
  "edge.synced": "synced",
  "edge.resyncQueued": "Resync queued for {domain}",
  "edge.resyncFailed": "Resync failed",
  "edge.resync": "Resync",
  "edge.b.notConfigured": "not configured",
  "edge.b.tracks": "tracks latest",
  "edge.b.inSync": "in sync",
  "edge.b.lagging": "lagging",
  "edge.configured": "configured",
  "edge.notSet": "not set",
  "ov.st.bound": "bound",
  "ov.st.skipped": "skipped",
  "ov.st.refused": "refused",
  "ov.st.binding": "binding",
  "ov.st.queued": "queued",
  "ov.st.failed": "failed",
  "ov.st.diverged": "diverged",
  "ov.st.timeout": "timeout",
  "ov.st.running": "running",
  "ov.st.halted": "halted",
  "ov.st.completed": "completed",
  "ov.toOrigin": "{name}: to its origin",
  "ov.backOnWorker": "{name}: back on the Worker",
  "ov.allToOrigin": "Every overlay domain → origin",
  "ov.globalOff": "Global kill switch off",
  "ov.killFailed": "Kill switch failed: {error}",
  "ov.kills": "Kill switches",
  "ov.kills.hint": "Send an overlay domain — or all of them — straight to its origin. Takes effect within ~70 s. Direct domains are never touched.",
  "ov.global.on": "Global switch ON — turn off",
  "ov.global.off": "Global switch: all overlay → origin",
  "ov.kills.error": "Kill switches unavailable",
  "ov.domain.ph": "domain.example — to its origin",
  "ov.switchOrigin": "Switch to origin",
  "ov.confirmAll": "Send every overlay domain to its origin?",
  "ov.confirmAll.hint": "The Worker steps aside on all overlay domains within ~70 s. Direct domains are not touched.",
  "ov.confirmAll.ok": "All overlay → origin",
  "ov.rollout.error": "Rollout unavailable",
  "ov.waveLine": "wave {i} / {n} · {size} per wave · pause {pause}s",
  "ov.stopping": "Stopping…",
  "ov.stop": "Stop",
  "ov.wave": "Wave",
  "ov.originBeforeAfter": "Origin before → after",
  "ov.started": "Rollout started",
  "ov.refused": "Rollout refused: {error}",
  "ov.rollouts": "Rollouts",
  "ov.rollouts.hint": "Put the Worker in front of domains in waves. For each domain: vibe-domains confirms the zone can take it, the origin answers /robots.txt, the overlay domain is bound, and the origin is asked again through the Worker. A failed or late binding, or a different answer, halts the rollout and sends that domain to its origin. Canary 5 → 50 → the rest.",
  "ov.domains.ph": "one domain per line\nlow-traffic first",
  "ov.perWave": "Per wave",
  "ov.pause": "Pause, s",
  "ov.bindTimeout": "Bind timeout, s",
  "ov.start": "Start ({n})",
  "ov.confirmStart": "Start a rollout over {n} domains?",
  "ov.confirmStart.hint": "{size} per wave, {pause} s between waves. A failed or diverging domain halts the rollout and goes back to its origin.",
  "ov.confirmStart.ok": "Start rollout",
  "ov.rollouts.error": "Rollouts unavailable",
  "ov.summary": "{n} domains · bound {bound} · refused {refused} · {who}",
  "ov.title": "Overlay",
  "ov.subtitle": "Domains whose own origin keeps serving everything the tracker does not.",
  "fw.s.tor_exit": "Tor exit",
  "fw.s.tor_exit.desc": "IP is a known Tor exit node.",
  "fw.s.residential_proxy": "Residential proxy",
  "fw.s.residential_proxy.desc": "Rotating residential proxy network.",
  "fw.s.public_proxy": "Public proxy",
  "fw.s.public_proxy.desc": "Open / public HTTP(S) proxy.",
  "fw.s.datacenter_ip": "Datacenter IP",
  "fw.s.datacenter_ip.desc": "Hosting range, not a consumer ISP.",
  "fw.s.vpn": "VPN",
  "fw.s.vpn.desc": "Commercial VPN egress.",
  "fw.s.hosting_asn": "Hosting ASN",
  "fw.s.hosting_asn.desc": "ASN classified as hosting/cloud.",
  "fw.s.ua_anomaly": "UA anomaly",
  "fw.s.ua_anomaly.desc": "User-agent is malformed or non-browser.",
  "fw.s.headless": "Headless",
  "fw.s.headless.desc": "Headless browser / automation fingerprint.",
  "fw.s.header_anomaly": "Header anomaly",
  "fw.s.header_anomaly.desc": "Graded: the share of browser-shape header checks this request failed.",
  "fw.s.ja4_ua_mismatch": "JA4 ↔ UA mismatch",
  "fw.s.ja4_ua_mismatch.desc": "TLS fingerprint inconsistent with the claimed browser.",
  "fw.s.country_asn_mismatch": "Country ↔ ASN mismatch",
  "fw.s.country_asn_mismatch.desc": "Geo country disagrees with ASN registration.",
  "fw.s.ip_reputation": "IP reputation",
  "fw.s.ip_reputation.desc": "Multiplied by the feed reputation score (0..1).",
  "fw.s.velocity": "Velocity",
  "fw.s.velocity.desc": "Multiplied by clicks-per-60s over the saturation point.",
  "fw.s.has_fbclid": "Has fbclid",
  "fw.s.has_fbclid.desc": "Facebook's own redirect mints this parameter, so carrying one is evidence of a real click. Negative by design.",
  "fw.s.referrer_spoof": "Referrer spoof",
  "fw.s.referrer_spoof.desc": "The referrer claims a network the click id does not belong to.",
  "fw.g.proxy": "Proxy & anonymity",
  "fw.g.client": "Client shape",
  "fw.g.graded": "Graded",
  "fw.g.human": "Looks human",
  "fw.g.other": "Other",
  "fw.thresholds.invalid": "The suspicious threshold must be below the bot threshold (0–100).",
  "fw.saved": "Scoring catalog saved",
  "fw.title": "Tune the scoring catalog",
  "fw.scope.global": "Editing the global default catalog (platform-wide).",
  "fw.scope.ws": "Editing this workspace's scoring catalog (it overrides the global default).",
  "fw.scope.inherit": "This workspace inherits the global default — saving creates a workspace override.",
  "fw.origin": "Origin:",
  "fw.lastBy": "last by {who}",
  "fw.thresholds": "Classification thresholds",
  "fw.suspiciousAt": "Suspicious at",
  "fw.botAt": "Bot at",
  "fw.thresholds.below": "The suspicious threshold must be below the bot threshold.",
  "fw.reset": "Reset to defaults",
  "fw.save": "Save catalog",
  "fw.o.default": "shipped default",
  "fw.o.manual": "manual edit",
  "fw.o.feedback": "tuned by the feedback loop",
  "fw.unavailable": "unavailable",
  "fw.shadow": "shadow",
  "fw.off": "off",
  "fw.default": "default {w}",
  "fw.saturates": "saturates at",
  "fw.shadow.hint": "Evaluated and traced in full, contributes 0. How a signal earns its weight before it routes anything.",
  "fw.disabled": "disabled",
  "fw.disabled.hint": "Not evaluated at all. Past traces mentioning it stay readable.",
  "fw.noTraffic": "No scored traffic in the selected period to preview against.",
  "fw.human": "Human {p}",
  "fw.suspicious": "Suspicious {p}",
  "fw.bot": "Bot {p}",
  "rep.any": "any",
  "rep.st.shadow": "shadow",
  "rep.st.active": "active",
  "rep.st.retracted": "retracted",
  "rep.st.allowlisted": "allowlisted",
  "rep.ty.net": "network",
  "rep.ty.ip": "IP",
  "rep.ty.ua": "UA hash",
  "rep.ty.visitor": "visitor",
  "rep.entries": "{n} entries",
  "rep.empty": "Nothing in the reputation store yet.",
  "rep.empty.hint": "Detectors run every five minutes over our own traffic. Everything they find lands in shadow — visible here, routing nothing.",
  "rep.entity": "Entity",
  "rep.detector": "Detector",
  "rep.confidence": "Confidence",
  "rep.evidence": "Evidence",
  "rep.lastSeen": "Last seen",
  "rep.st.shadow.hint": "Visible in every decision trace, routing nothing.",
  "rep.st.active.hint": "Contributing to the bot score.",
  "rep.st.retracted.hint": "A human said this was wrong. It never scores again.",
  "rep.st.allowlisted.hint": "A human said we will never be right about this.",
  "rep.reasonRequired": "A reason is required — a change nobody can explain later cannot safely be undone.",
  "rep.changed": "Now: {status}",
  "rep.summary": "{p} confidence · {detector} · {status}",
  "rep.noDetector": "no detector",
  "rep.noObs": "No observations recorded.",
  "rep.audit": "Audit trail",
  "rep.by": "by {who}",
  "rep.change": "Change the verdict",
  "rep.reason.ph": "Why? (required — this is what makes the change reversible later)",
  "rep.promote": "Promote to active",
  "rep.retract": "Retract",
  "rep.allowlist": "Allowlist",
  "rep.actions.hint": "Retract undoes one wrong verdict. Allowlist says we will never be right about this entity — detectors keep observing it, so the evidence still helps measure them, but it never scores.",
  "sc.empty": "No detector has had an opinion about traffic in this period yet.",
  "sc.empty.hint": "Detectors run every five minutes and land their findings in shadow. Nothing is suppressed until a detector earns a promotion here.",
  "sc.baseline": "Baseline conversion rate:",
  "sc.baseline.hint": "over {n} clicks. A detector whose flagged traffic converts at or above this is finding customers, not bots.",
  "sc.entities": "Entities",
  "sc.flagged": "Flagged",
  "sc.share": "Share",
  "sc.converted": "Converted",
  "sc.cr": "Conv. rate",
  "sc.retracted": "Retracted",
  "sc.verdict": "Verdict",
  "sc.v.thin": "too little evidence",
  "sc.v.thin.why": "Fewer than 200 flagged clicks. Neither a promotion nor a demotion can be justified on this much traffic.",
  "sc.v.retracted": "{n} human retraction(s)",
  "sc.v.retracted.why": "Somebody already looked at this detector's output and said it was wrong. That is more direct evidence than any conversion rate.",
  "sc.v.customers": "flagging customers",
  "sc.v.customers.why": "Flagged traffic converts at {rate} against a {base} baseline. This detector is not finding bots.",
  "sc.v.promote": "ready to promote",
  "sc.v.promote.why": "Flagged traffic converts at {rate}, far below the {base} baseline. Run 7 days before promoting.",
  "sc.v.watch": "keep watching",
  "sc.v.watch.why": "Flagged traffic converts below baseline but not decisively. A longer window is needed.",
  "int.cf.tagline": "DNS zones, proxied records, SSL and edge serving for your tracking domains.",
  "int.cf.setup": "Create one API token with these scopes: Zone:Edit, DNS:Edit, SSL and Certificates:Edit (DNS + SSL), Workers KV Storage:Edit + Workers Routes:Edit + Workers Scripts:Read (edge serving). The account ID is on the right side of any zone's Overview page. For the fastest global serving, also add R2 credentials below.",
  "int.f.apiToken": "API token",
  "int.f.accountId": "Account ID",
  "int.f.accountId.hint": "Required for edge serving (Workers KV and routes live at the account level).",
  "int.f.zoneId": "Zone ID",
  "int.f.zoneId.ph": "(optional) zone for custom hostnames",
  "int.f.zoneId.hint": "Only needed when connecting external domains via CF for SaaS custom hostnames.",
  "int.f.r2key.ph": "(optional) R2 S3 access key",
  "int.f.r2key.hint": "R2 → Manage R2 API Tokens → Object Read & Write. Lets landers serve from every PoP globally (~15 ms) instead of the origin. The endpoint is derived from the account ID.",
  "int.f.r2secret.ph": "(optional) R2 S3 secret",
  "int.f.r2secret.hint": "Paired with the R2 access key above.",
  "int.f.r2bucket.hint": "Bucket the edge Worker reads. Defaults to edge-pages.",
  "int.or.tagline": "Alert-rule and flow assistants.",
  "int.or.setup": "Create an API key at openrouter.ai/keys. Optionally pin a model — the platform default is a fast, low-cost one.",
  "int.f.apiKey": "API key",
  "int.f.model": "Model",
  "int.cat.ai": "AI",
  "int.st.healthy": "Healthy",
  "int.st.degraded": "Degraded",
  "int.st.failing": "Failing",
  "int.st.disabled": "Disabled",
  "conn.probeFailed": "probe failed",
  "conn.updated": "{provider} connection updated",
  "conn.platformDefault": "Platform default",
  "conn.wsName": "{provider} — workspace",
  "conn.connected": "{provider} connected",
  "conn.connectTitle": "Connect {provider}",
  "conn.platform.hint": "Shared fallback for every workspace without its own connection. Platform admins only.",
  "conn.docs": "Provider docs",
  "conn.encrypted": "Credentials are encrypted at rest (AES-256-GCM) and never returned to the browser.",
  "conn.verified": "Connection verified — the credentials work.",
  "conn.fillIn": "Fill in: {fields}",
  "conn.probe.hint": "Check the provider with these credentials",
  "conn.testing": "Testing…",
  "conn.test": "Test connection",
  "conn.connect": "Connect",
  "conn.optional": "(optional)",
  "conn.replace": "Replace",
  "conn.newValue.ph": "enter a new value (the stored one is kept if untouched)",
  "conn.wsConnection": "Workspace connection",
  "conn.fallback": "the platform default",
  "conn.usingShared": "Using the platform's shared {provider} account.",
  "conn.override": "Use my own",
  "conn.notConfigured": "Not configured",
  "conn.noDefault": "No platform default — workspaces without their own connection get an error.",
  "conn.setDefault": "Set default",
  "conn.ok": "{label}: connection OK",
  "conn.removedFallback": "Removed — this workspace now uses {fallback}.",
  "conn.removed": "Connection removed.",
  "conn.checked": "checked {when}",
  "conn.neverTested": "never tested",
  "conn.probeStored": "Check the stored credentials",
  "conn.testShort": "Test",
  "conn.editCreds": "Edit credentials",
  "conn.remove": "Remove this connection",
  "conn.sure": "Sure?",
  "conn.title": "Integrations",
  "conn.subtitle": "Connect this workspace to Cloudflare and OpenRouter. Credentials are encrypted, belong to your workspace, and fall back to the platform default when you have not connected your own account.",
  "conn.count": "{n}/{total} connected",
  "conn.admin.hint": "You can edit the platform-default connections every workspace falls back to.",
  "conn.admin": "Platform admin",
  "conn.loadError": "Failed to load integrations",
  "mac.click": "Click identity",
  "mac.campaign": "Campaign & payout",
  "mac.traffic_source": "Traffic source",
  "mac.lander_offer": "Lander & offer",
  "mac.device": "Device & UA",
  "mac.geo": "Geo & locale",
  "mac.network": "Network",
  "mac.tracking_field": "Tracking fields",
  "mac.deeplink": "Deeplink",
  "mac.custom": "Custom",
  "tfm.empty": "No tracking fields mapped yet. Press «Add tracking field» below to bind a source macro to a trackingField slot.",
  "tfm.add": "Add tracking field",
};
